import { authorizedFetch } from "@/utils/fetch";

const baseUrl = import.meta.env.VITE_API_URL;
const baseUrlV1 = `${baseUrl}/api/v1`;

export type PostListRespoonse = {
  posts: Post[];
  count: number;
};

export type Post = {
  id: string;
  teaser: string;
  mediaType: string;
  mediaUrl: string;
  thumbnailUrl: string;
  createdAt: string;
  countOfComments: number;
  countOfUnsafeComments: number;
  countOfDeletedComments: number;
  averageOfUnsafetyScore: number;
};

export type TopPost = {
  author: string;
  averageOfUnsafetyScore: number;
  countOfAcceptedComments: number;
  countOfComments: number;
  countOfDeletedComments: number;
  countOfOpenComments: number;
  countOfUnsafeComments: number;
  createdAt: string;
  id: number;
  mediaType: string;
  mediaUrl: string;
  socialMediaType: number;
  teaser: string;
  thumbnailUrl: string;
};

export type PostListFilter = {
  searchTerm?: string;
  sortBy?: string;
  sortDirection?: string;
  from?: string;
  to?: string;
  statusFilter?: string;
  statuses?: string[];
  visibilities?: string[];
  statusConnection?: string;
  folder?: string;
  attributes?: string[];
};

export type PostCountValues = {
  folders: Record<string, number>;
};

export enum PostListItemMetricType {
  COMMENTS = 1,
  UNSAFE_COMMENTS = 2,
  DELETED_COMMENTS = 3,
  AVERAGE_UNSAFETY_SCORE = 4,
}

export const getPosts = async (
  socialMediaTypeId: number,
  socialMediaConnectionId: number,
  page: number,
  pageSize: number,
  filter?: PostListFilter,
): Promise<PostListRespoonse> => {
  const params = new URLSearchParams();
  params.set("smc", socialMediaConnectionId.toString());
  params.set("smt", socialMediaTypeId.toString());

  if (filter?.searchTerm) params.set("searchTerm", filter.searchTerm);
  if (filter?.sortBy) params.set("sortBy", filter.sortBy);
  if (filter?.sortDirection) params.set("sortDirection", filter.sortDirection);
  if (filter?.from) params.set("from", filter.from);
  if (filter?.to) params.set("to", filter.to);
  if (filter?.statusFilter) params.set("status-filter", filter.statusFilter);
  if (filter?.statuses) filter.statuses.forEach((item) => params.append("status", item));
  if (filter?.visibilities) params.set("comment-properties", filter.visibilities.join(","));
  if (filter?.attributes) params.set("ab", filter.attributes.join(","));
  if (filter?.statusConnection) params.set("status-connection", filter.statusConnection);
  if (filter?.folder) params.set("folder", filter.folder);

  params.set("page", page.toString());
  params.set("size", pageSize.toString());

  const rawResponse = await authorizedFetch(`${baseUrlV1}/posts?${params.toString()}`);
  const response = await rawResponse.json();
  return response;
};

export const getPost = async (
  id: string,
  socialMediaTypeId: number,
  socialMediaConnectionId: number,
): Promise<Post> => {
  const params = new URLSearchParams();
  params.set("smc", socialMediaConnectionId.toString());
  params.set("smt", socialMediaTypeId.toString());

  const response = await authorizedFetch(`${baseUrlV1}/posts/${id}?${params.toString()}`);
  const post = await response.json();
  return post;
};

export const getTopPosts = async (
  limit: string,
  sortBy?: string,
  sortDirection?: string,
): Promise<TopPost[]> => {
  const params = new URLSearchParams();
  if (limit) params.set("limit", limit);
  if (sortBy) params.set("sortBy", sortBy);
  if (sortDirection) params.set("sortDirection", sortDirection);

  const response = await authorizedFetch(`${baseUrlV1}/posts/top?${params.toString()}`);
  const posts = await response.json();
  return posts.posts;
};

export const getNumberOfPosts = async (
  socialMediaTypeId: number,
  socialMediaConnectionId: number,
): Promise<PostCountValues> => {
  const params = new URLSearchParams();
  params.set("smc", socialMediaConnectionId.toString());
  params.set("smt", socialMediaTypeId.toString());

  const response = await authorizedFetch(`${baseUrlV1}/posts/count?${params.toString()}`);
  const counts: PostCountValues = await response.json();
  return counts;
};
